import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { childrenApi } from '@/lib/children-api'
import { updatesApi } from '@/lib/updates-api'
import { parentApi, type ParentChild } from '@/lib/parent-api'
import { invitationsApi, type PendingInvitation } from '@/lib/invitations-api'
import { SkeletonCard } from '@/components/common'
import { useDeferredLoading } from '@/hooks/useDeferredLoading'
import { trackEvent, setUserProperties, Events } from '@/lib/observability'
import type { Update } from '@/types/update'
import type { Child } from '@/types/child'

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <SkeletonCard className="h-20" />
        <SkeletonCard className="h-20" />
      </div>
      <div className="flex gap-2">
        <div className="h-10 w-28 rounded-lg bg-gray-200 animate-pulse" />
        <div className="h-10 w-28 rounded-lg bg-gray-200 animate-pulse" />
      </div>
      <div className="space-y-3">
        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
        <SkeletonCard className="h-16" />
        <SkeletonCard className="h-16" />
      </div>
    </div>
  )
}

function ChildminderDashboard() {
  const [children, setChildren] = useState<Child[]>([])
  const [updates, setUpdates] = useState<Update[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const showLoading = useDeferredLoading(isLoading)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [childrenRes, updatesRes] = await Promise.all([
        childrenApi.list(),
        updatesApi.list(), // Gets last 2 days by default
      ])
      setChildren(childrenRes.children)
      setUpdates(updatesRes.updates)

      // Track dashboard view and update user properties
      trackEvent(Events.DASHBOARD_VIEWED, {
        child_count: childrenRes.children.length,
        update_count: updatesRes.updates.length,
      })
      setUserProperties({
        childCount: childrenRes.children.length,
        plan: childrenRes.subscription.tier as 'free' | 'starter' | 'professional',
        hasActiveSubscription: childrenRes.subscription.tier !== 'free',
      })
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false)
    }
  }

  if (showLoading) {
    return <DashboardSkeleton />
  }

  // Still loading but not showing skeleton yet - render nothing to prevent flash
  if (isLoading) {
    return null
  }

  // Calculate today's stats
  const today = new Date().toDateString()
  const todaysUpdates = updates.filter(
    (u) => new Date(u.createdAt).toDateString() === today
  )

  // Find children without updates today
  const childrenWithUpdatesToday = new Set(
    todaysUpdates.flatMap((u) => u.children.map((c) => c.id))
  )
  const childrenWithoutUpdates = children.filter(
    (c) => !childrenWithUpdatesToday.has(c.id)
  )

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <section className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
          <p className="text-3xl font-bold text-primary-600">{children.length}</p>
          <p className="text-sm text-gray-500">Children</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{todaysUpdates.length}</p>
          <p className="text-sm text-gray-500">Updates Today</p>
        </div>
      </section>

      {/* Quick Actions */}
      <section>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/updates"
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white active:bg-primary-700"
          >
            + Add Update
          </Link>
          <Link
            to="/children/new"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 active:bg-gray-50"
          >
            + Add Child
          </Link>
        </div>
      </section>

      {/* Children Needing Updates */}
      {childrenWithoutUpdates.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-medium text-amber-700">
            Need updates today
          </h2>
          <div className="flex flex-wrap gap-2">
            {childrenWithoutUpdates.map((child) => (
              <Link
                key={child.id}
                to={`/children/${child.id}`}
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 active:bg-amber-100 ph-no-capture"
              >
                {child.firstName}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function ParentDashboard() {
  const user = useAuthStore((state) => state.user)
  const isEmailVerified = user?.emailVerified ?? false

  const [children, setChildren] = useState<ParentChild[]>([])
  const [updates, setUpdates] = useState<Update[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const showLoading = useDeferredLoading(isLoading)

  useEffect(() => {
    loadData()
  }, [isEmailVerified])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [childrenRes, invitationsRes] = await Promise.all([
        parentApi.getChildren(),
        invitationsApi.getPendingInvitations(),
      ])
      setChildren(childrenRes.children)
      setPendingInvitations(invitationsRes.invitations)

      // Only load updates if verified
      if (isEmailVerified && childrenRes.children.length > 0) {
        try {
          const updatesRes = await parentApi.getUpdates()
          setUpdates(updatesRes.updates)
        } catch {
          setUpdates([])
        }
      }

      // Track dashboard view and update user properties
      trackEvent(Events.DASHBOARD_VIEWED, {
        child_count: childrenRes.children.length,
        pending_invitations: invitationsRes.invitations.length,
      })
      setUserProperties({ childCount: childrenRes.children.length })
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptInvitation = async (invitation: PendingInvitation) => {
    try {
      await invitationsApi.acceptInvitation(invitation.token)
      setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id))
      trackEvent(Events.INVITATION_ACCEPTED, { child_name: invitation.childName })
      // Reload data to get the new child
      loadData()
    } catch {
      // Handle error
    }
  }

  const handleDeclineInvitation = async (invitation: PendingInvitation) => {
    try {
      await invitationsApi.declineInvitation(invitation.token)
      setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id))
      trackEvent(Events.INVITATION_DECLINED, { child_name: invitation.childName })
    } catch {
      // Handle error
    }
  }

  if (showLoading) {
    return <DashboardSkeleton />
  }

  // Still loading but not showing skeleton yet - render nothing to prevent flash
  if (isLoading) {
    return null
  }

  // Calculate today's stats
  const today = new Date().toDateString()
  const todaysUpdates = updates.filter(
    (u) => new Date(u.createdAt).toDateString() === today
  )

  const hasNoContent = children.length === 0 && pendingInvitations.length === 0

  return (
    <div className="space-y-6">
      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h2 className="mb-3 font-medium text-amber-800">
            Pending Invitations ({pendingInvitations.length})
          </h2>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between rounded-lg bg-white p-3 shadow-sm"
              >
                <div>
                  <p className="font-medium">{invitation.childName}</p>
                  <p className="text-sm text-gray-500">
                    From {invitation.childminderEmail}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAcceptInvitation(invitation)}
                    className="rounded bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleDeclineInvitation(invitation)}
                    className="rounded bg-gray-200 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-300"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* No Content State */}
      {hasNoContent && (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
          <p className="text-lg font-medium text-gray-600">Welcome to Brightseed!</p>
          <p className="mt-2 text-gray-500">
            Your childminder will send you an invitation to connect your account.
          </p>
          <p className="mt-1 text-sm text-gray-400">
            Once connected, you'll see updates about your children here.
          </p>
        </div>
      )}

      {/* Quick Stats */}
      {children.length > 0 && (
        <section className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
            <p className="text-3xl font-bold text-primary-600">{children.length}</p>
            <p className="text-sm text-gray-500">
              {children.length === 1 ? 'Child' : 'Children'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{todaysUpdates.length}</p>
            <p className="text-sm text-gray-500">Updates Today</p>
          </div>
        </section>
      )}

      {/* Children Cards */}
      {children.length > 0 && (
        <section>
          <h2 className="mb-3 text-lg font-medium">Your Children</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {children.map((child) => {
              const childUpdates = todaysUpdates.filter((u) =>
                u.children.some((c) => c.id === child.id)
              )
              return (
                <Link
                  key={child.id}
                  to={`/my-children/${child.id}`}
                  className="rounded-lg border border-gray-200 bg-white p-4 active:bg-gray-50"
                >
                  <p className="text-lg font-medium ph-no-capture">
                    {child.firstName} {child.lastName}
                  </p>
                  {childUpdates.length > 0 ? (
                    <p className="mt-1 text-sm text-green-600">
                      {childUpdates.length} update{childUpdates.length !== 1 ? 's' : ''} today
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-gray-400">No updates today</p>
                  )}
                </Link>
              )
            })}
          </div>
        </section>
      )}

      {/* Email Verification Warning */}
      {!isEmailVerified && children.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
          <p className="font-medium text-amber-800">Verify your email to see updates</p>
          <p className="mt-1 text-sm text-amber-600">
            Check your inbox for the verification link
          </p>
        </div>
      )}
    </div>
  )
}

export function DashboardPage() {
  const { user } = useAuthStore()

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">
        {user?.role === 'childminder' ? 'Dashboard' : 'Home'}
      </h1>

      {user?.role === 'childminder' && <ChildminderDashboard />}
      {user?.role === 'parent' && <ParentDashboard />}
    </div>
  )
}
